import React, { useState, useEffect } from 'react';
import {
  TextInput,
  Checkbox,
  Form,
  Button,
  InlineLoading
} from 'carbon-components-react';
import { useFormik } from 'formik';
import _ from 'lodash';

interface FormValues {
  userGroup: string;
  managerGroup: string;
  orgAdminGroup: string;
  isOnlyMatchRole: boolean;
  isAllowAutoJoin: boolean;
}

const SAMLMapping: React.FC<Props> = ({
  organisation,
  handleRoleMappingSubmit,
  updateOrgConfiguration,
  configurationUpdateType,
  isUpdatingConfiguration
}) => {
  const orgName = organisation?.organisation_name;
  const onlyAllowMatchedRole = organisation?.is_only_match_role;
  const allowAutoJoin = organisation?.is_allow_auto_join;
  const [localSavingSSORoleMapping, setLocalSavingSSORoleMapping] =
    useState(false);

  const SSORoleMap = _.cloneDeep(organisation?.Ssorolemap);
  const SSORoleMapOrdered = _.orderBy(
    SSORoleMap,
    ['ref_user_role_id'],
    ['asc']
  );

  const initialValues = {
    userGroup: SSORoleMapOrdered[0]?.name,
    managerGroup: SSORoleMapOrdered[1]?.name,
    orgAdminGroup: SSORoleMapOrdered[2]?.name,
    isOnlyMatchRole: onlyAllowMatchedRole,
    isAllowAutoJoin: allowAutoJoin
  };

  const { values, setFieldValue, handleSubmit, setFieldTouched } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: initialValues,
      onSubmit: (values: FormValues) => {
        setLocalSavingSSORoleMapping(true);
        handleRoleMappingSubmit(values);
      }
    });

  const handleTextInputChange = (role: any, val: string) => {
    if (role === 'User') {
      setFieldValue('userGroup', val);
      setFieldTouched('userGroup');
    } else if (role === 'Manager') {
      setFieldValue('managerGroup', val);
      setFieldTouched('managerGroup');
    } else if (role === 'Organisation Admin') {
      setFieldValue('orgAdminGroup', val);
      setFieldTouched('orgAdminGroup');
    }
  };

  const handleCheckboxChange = (type: string, checked: boolean) => {
    if (type === 'isAllowAutoJoin') {
      setFieldValue('isAllowAutoJoin', checked);
      const data = {
        is_allow_auto_join: checked
      };
      updateOrgConfiguration(data);
    } else if (type === 'samlGroupAccess') {
      setFieldValue('isOnlyMatchRole', checked);
      const data = {
        is_only_match_role: checked
      };
      updateOrgConfiguration(data);
    }
  };

  useEffect(() => {
    if (
      isUpdatingConfiguration &&
      configurationUpdateType === 'ssoRoleMapping'
    ) {
      setLocalSavingSSORoleMapping(true);
    } else if (
      !isUpdatingConfiguration &&
      configurationUpdateType === 'ssoRoleMapping'
    ) {
      setTimeout(() => {
        setLocalSavingSSORoleMapping(false);
      }, 2000);
    }
  }, [organisation, isUpdatingConfiguration, configurationUpdateType]);

  return (
    <Form onSubmit={handleSubmit}>
      <div id="authentication" className="saml-mapping spacing-padding">
        <div className="main-section-title">Authentication</div>
        <div className="checkbox-container">
          <Checkbox
            labelText={`Allow users from my enterprise ${orgName} to join this organisation`}
            id="allow-auto-join"
            onChange={(checked: boolean) =>
              handleCheckboxChange('isAllowAutoJoin', checked)
            }
            checked={values.isAllowAutoJoin}
          />
        </div>
        <div
          className={`sub-section-title extra-margin-top ${
            !values.isAllowAutoJoin ? 'disabled' : ''
          }`}
        >
          SAML (SSO) group role mapping
        </div>
        <div className="textinput-container">
          <TextInput
            id="userGroup"
            name="userGroup"
            className="textinput-custom-style"
            labelText="USER"
            helperText="Enter an Active Directory group name for USERS"
            placeholder="AD GROUP NAME"
            onChange={(val: any) =>
              handleTextInputChange('User', val.target.value)
            }
            value={values.userGroup}
            disabled={!values.isAllowAutoJoin}
          />
        </div>
        <div className="textinput-container">
          <TextInput
            id="managerGroup"
            name="managerGroup"
            className="textinput-custom-style"
            labelText="MANAGER"
            helperText="Enter an Active Directory group name for MANAGERS"
            placeholder="AD GROUP NAME"
            onChange={(val: any) =>
              handleTextInputChange('Manager', val.target.value)
            }
            value={values.managerGroup}
            disabled={!values.isAllowAutoJoin}
          />
        </div>
        <div className="textinput-container">
          <TextInput
            id="orgAdminGroup"
            name="orgAdminGroup"
            className="textinput-custom-style"
            labelText="ORGANISATION ADMINISTRATOR"
            helperText="Enter an Active Directory group name for ORGANISATION ADMINISTRATORS"
            placeholder="AD GROUP NAME"
            onChange={(val: any) =>
              handleTextInputChange('Organisation Admin', val.target.value)
            }
            value={values.orgAdminGroup}
            disabled={!values.isAllowAutoJoin}
          />
        </div>
        <div className="button-container">
          <Button
            id="ssoRoleMappingButton"
            type="submit"
            kind="tertiary"
            size="default"
            className="button-size"
            disabled={
              !values.isAllowAutoJoin ||
              (isUpdatingConfiguration &&
                configurationUpdateType === 'ssoRoleMapping') ||
              localSavingSSORoleMapping
            }
          >
            {!(
              isUpdatingConfiguration &&
              configurationUpdateType === 'ssoRoleMapping'
            ) && !localSavingSSORoleMapping ? (
              'Save mapping'
            ) : (
              <InlineLoading
                description={
                  !(
                    isUpdatingConfiguration &&
                    configurationUpdateType === 'ssoRoleMapping'
                  )
                    ? 'Mapping saved'
                    : 'Saving...'
                }
                status={
                  !(
                    isUpdatingConfiguration &&
                    configurationUpdateType === 'ssoRoleMapping'
                  )
                    ? 'finished'
                    : 'active'
                }
                aria-live={'assertive'}
              />
            )}
          </Button>
        </div>
        <div className="checkbox-container">
          <Checkbox
            labelText="Only allow users from these groups (users with no group will not be allowed)"
            id="samlGroupAccess"
            onChange={(checked: boolean) =>
              handleCheckboxChange('samlGroupAccess', checked)
            }
            checked={values.isOnlyMatchRole}
            disabled={!values.isAllowAutoJoin}
          />
        </div>
      </div>
    </Form>
  );
};

interface Props {
  user: any;
  organisation: any;
  handleRoleMappingSubmit: Function;
  updateOrgConfiguration: Function;
  configurationUpdateType: string;
  isUpdatingConfiguration: boolean;
}

export default React.memo(SAMLMapping);
